import React, { useEffect, useRef } from 'react';

// Quill is loaded via CDN in index.html — this component wraps it
function RichTextEditor({ value, onChange, placeholder = 'Write here…', minHeight = 320 }) {
    const containerRef = useRef(null);
    const quillRef = useRef(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        if (!window.Quill || quillRef.current) return;

        const quill = new window.Quill(containerRef.current, {
            theme: 'snow',
            placeholder,
            modules: {
                toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    [{ size: ['small', false, 'large', 'huge'] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ color: [] }, { background: [] }],
                    [{ align: [] }],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    [{ indent: '-1' }, { indent: '+1' }],
                    ['blockquote', 'code-block'],
                    ['link', 'image'],
                    ['clean'],
                ],
            },
        });

        quillRef.current = quill;

        // Replace default base64 image handler with a real file upload
        quill.getModule('toolbar').addHandler('image', () => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/jpeg,image/png,image/gif,image/webp';
            input.click();
            input.onchange = async () => {
                const file = input.files[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('image', file);
                const token = localStorage.getItem('token');
                const apiUrl = process.env.REACT_APP_API_URL || '/api';
                try {
                    const response = await fetch(`${apiUrl}/kb/upload-image`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}` },
                        body: formData,
                    });
                    const data = await response.json();
                    if (data.url) {
                        const range = quill.getSelection() || { index: quill.getLength() };
                        quill.insertEmbed(range.index, 'image', data.url);
                        quill.setSelection(range.index + 1);
                    }
                } catch (err) {
                    console.error('Image upload failed:', err);
                }
            };
        });

        // Set initial value
        if (value) {
            quill.root.innerHTML = value;
        }

        quill.on('text-change', () => {
            const html = quill.root.innerHTML;
            onChangeRef.current(html === '<p><br></p>' ? '' : html);
        });

        return () => {
            // Quill doesn't have a destroy, just clear the ref
            quillRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync value from outside only when the editor doesn't have focus
    useEffect(() => {
        const quill = quillRef.current;
        if (!quill) return;
        if (document.activeElement && quill.root.contains(document.activeElement)) return;
        const current = quill.root.innerHTML;
        const next = value || '';
        if (current !== next) {
            quill.root.innerHTML = next;
        }
    }, [value]);

    return (
        <div className="rich-text-editor-wrapper">
            <div ref={containerRef} style={{ minHeight }} />
        </div>
    );
}

export default RichTextEditor;
